'use client';

import { Link } from '@/i18n/routing';
import { ArrowRight } from 'lucide-react';
import { useTranslations } from '@/i18n/intl';
import { stripHtml } from '@/lib/utils';
import { resolveMediaUrl } from '@/lib/media';

interface InsightCardProps {
  slug?: string; // Used for dynamic links
  title: string;
  description: string;
  href?: string; // Manual href if provided
  image?: string;
  category?: string;
  date?: string;
  footprints?: any[];
  showDescription?: boolean;
}

export function InsightCard({ 
  slug, 
  title, 
  description, 
  href, 
  image, 
  category, 
  date,
  footprints = [],
  showDescription = true
}: InsightCardProps) {
  const finalHref = href || (slug ? `/insights/${slug}` : '#');
  const tCommon = useTranslations('Common');
  const cleanDescription = stripHtml(description);

  return (
    <Link
      href={finalHref as any}
      className="group relative flex flex-col min-h-[280px] overflow-hidden rounded-xl shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100"
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
        style={{
          backgroundImage: image
            ? `url(${resolveMediaUrl(image)})`
            : 'linear-gradient(135deg, #0B1F44 0%, #226CD4 100%)',
        }}
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-navy/70 group-hover:bg-navy/60 transition-colors duration-500" />

      {/* Content */}
      <div className="relative p-6 md:p-8 z-10 flex-1 flex flex-col">
        <div className="flex-1">
          {(category || date) && (
            <div className="flex items-center gap-3 mb-3">
              {category && (
                <span className="text-[10px] uppercase tracking-[0.2em] text-white font-bold font-sans">
                  {category.replace('-', ' ')}
                </span>
              )}
              {category && date && <span className="w-1 h-1 rounded-full bg-white/30" />}
              {date && (
                <span className="text-[10px] text-white/50 font-sans uppercase tracking-wider">
                  {date}
                </span>
              )}
            </div>
          )}
          
          <h3
            className="text-[20px] md:text-[24px] font-bold text-white mb-3 leading-tight font-heading"
          >
            {title}
          </h3>

          {/* Industry Tags */}
          {footprints.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {footprints.slice(0, 2).map((f) => (
                <span key={f.id} className="text-[9px] bg-white/10 backdrop-blur-md text-white px-2 py-0.5 rounded border border-white/10 uppercase tracking-wider font-bold">
                  {f.title}
                </span>
              ))}
              {footprints.length > 2 && (
                <span className="text-[9px] text-white/40 font-bold">+{footprints.length - 2}</span>
              )}
            </div>
          )}

          {showDescription && (
            <p className="text-[13px] md:text-[14px] text-white/70 mb-5 leading-relaxed line-clamp-2 font-sans font-light">
              {cleanDescription}
            </p>
          )}
        </div>
        
        <span className="inline-flex items-center gap-2 text-[13px] font-bold text-white font-sans uppercase tracking-widest mt-6">
          {tCommon('readMore')}
          <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform duration-300" />
        </span>
      </div>
    </Link>
  );
}
